import { useEffect, useState } from 'react'

import { Helmet } from 'react-helmet'
import NavBar from 'components/navbar/NavBar'
import Header from 'components/typography/Header'
import styles from './styles/Pricing.css'
import Link from 'components/misc/Link'
import * as analytics from 'lib/analytics'
import Paragraph from '../components/typography/Paragraph'
import { useSelector } from 'react-redux'
import Endorsements from 'components/pricing/Endorsements'
import PlanTable from '../components/pricing/PlanTable'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { errorToast } from '../lib/toast'
import loginModalStore from 'store/login-modal'
import RedeemCode from '../components/pricing/RedeemCode'
import injectAsyncStore from 'lib/inject-async-store'

const DESCRIPTION_COPY = `
Simple and transparent pricing
Search and discover millions of citations showing expert insights and analyses on any topic and any publication.
`

const Plans = ({ user }) => (
  <>
    <PlanTable />
    {user?.plan === 'free' && !user?.trialActivated && (
      <Paragraph>
        After your free trial ends, you'll be automatically enrolled in your chosen plan. You can change or cancel your subscription during your trial period.
      </Paragraph>
    )}
    <Endorsements className={styles.endorsements} />
    <div className={styles.faq}>
      <img alt='recommend institution' width='76px' height='76px' src={`${CONFIG.assetsBaseURL}/images/icons/institution.svg`} />
      <div className={styles.specialOfferSubheading}>
        <Header className={styles.faqHeader} level={4} center>Help & FAQ</Header>
        <Paragraph className={styles.faqParagraph} type='small' light center>
          If you have any problems with your subscription feel free to contact us at <Link blue underline href='mailto:support@scite.ai'>support@scite.ai</Link> anytime and we will get right back to you.
          All prices in USD. Want us to issue an invoice? <Link blue underline href='mailto:support@scite.ai'>contact us</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>Do you offer group plans or discounts?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We do! If you are interested in a group plan, please contact us at <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link> for a quote
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>Do you offer student or academic discounts?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We do offer discounts for students and academics if they recommend their institution purchase a scite license and cc us or forward the email to <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>How do your payments work?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We use stripe to manage our payments, we do not store or manage credit card information.
        </Paragraph>
      </div>
    </div>
  </>
)

export const Pricing = () => {
  const location = useLocation()

  const user = useSelector(({ ui }) => ui.user)
  const [error, setError] = useState()
  const [redeemCodePage, setRedeemCodePage] = useState(false)

  useEffect(() => {
    const query = qs.parse(location.search)

    if (query.redeem) {
      setRedeemCodePage(true)
    }

    if (query.error) {
      let errorCopy = 'Something went wrong. Please try again or contact us at support@scite.ai'
      if (query.error === 'no-user') {
        errorCopy = 'You must be signed in to purchase a plan. If this continues to happen please contact us at support@scite.ai'
      } else if (query.error === 'select-price') {
        errorCopy = 'Please select a price to purchase for. If this continues to happen please contact us at support@scite.ai'
      }
      setError(errorCopy)
    }
  }, [])

  useEffect(() => {
    analytics.event({
      category: 'Pricing',
      action: 'View Pricing Page'
    })
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (error) {
      errorToast({
        message: error,
        durationMs: 5500
      })
    }
  }, [error])

  return (
    <div className={styles.container}>
      <Helmet>
        <title>scite pricing</title>

        <meta property='title' content='scite pricing' />
        <meta property='og:title' content='scite pricing' />
        <meta property='twitter:title' content='scite pricing' />

        <meta name='description' content={DESCRIPTION_COPY} />
        <meta property='og:description' content={DESCRIPTION_COPY} />
        <meta property='twitter:description' content={DESCRIPTION_COPY} />

        <meta property='og:image' content='http://image.thum.io/get/ogImage/https://scite.ai/pricing?bannerClosed=true&v=2' />
        <meta property='twitter:image' content='http://image.thum.io/get/ogImage/https://scite.ai/pricing?bannerClosed=true&v=2' />

      </Helmet>
      <NavBar staticPage homePage />
      <div className={styles.pricingPageLayout}>
        <Header level={3} className={styles.header} center>
          Pricing & Plans
        </Header>
        {!redeemCodePage
          ? (
            <div className={styles.plansMainContainer}>
              <Plans user={user} />
            </div>
            )
          : (
            <div>
              <Header level={5} center>
                Redeem your code to enable your scite plan
              </Header>
              <Paragraph center>
                Enter the code you have received from a scite partner below to activate your scite plan.
              </Paragraph>
              <RedeemCode />
            </div>
            )}
      </div>
    </div>
  )
}

export default injectAsyncStore({
  loginModal: loginModalStore
})(Pricing)
